import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { OwnerType } from '../entities/academy-enums.model';
import { PERIOD_KEY_PATTERN } from '../entities/academy-query.model';
import { UsageMetricResponse } from '../entities/academy.model';
import { AcademyODataService } from './academy-odata.service';

/**
 * HTTP client for the usage-metering area. Source of truth: the backend
 * `UsageMetricsController` (`academyservice-api`, spec 007-tenant-metering), a read-only
 * OData entity set (`/UsageMetrics`) plus two admin POST actions on that set
 * (`/UsageMetrics/aggregate`, `/UsageMetrics/close`). Reads go through the generic
 * {@link AcademyODataService} (OData `OPTIONS` query, `value`-envelope + bare-array
 * tolerant, enum-name normalized). `period` is `YYYY-MM`, validated against
 * `PERIOD_KEY_PATTERN` before any request leaves the client.
 */
@Injectable({ providedIn: 'root' })
export class MeteringService {
  private readonly http = inject(HttpClient);
  private readonly odata = inject(AcademyODataService);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;

  /**
   * `OPTIONS /UsageMetrics?$filter=PeriodKey eq '<period>'` — usage snapshots for the period,
   * isolated to the caller's scope by the server query filter. Returns the OData rows.
   */
  public usage(period: string): Observable<UsageMetricResponse[]> {
    this.assertPeriod(period);
    return this.odata
      .list<UsageMetricResponse>('UsageMetrics', {
        filter: `PeriodKey eq '${period}'`,
      })
      .pipe(map((list) => list.value));
  }

  /**
   * Reads usage for several periods at once — one OData query per key, merged into a
   * `{ [period]: rows }` map on a single emission (`forkJoin`). The entity set has no
   * multi-period filter helper, so the `Uso & Faturamento` trend/selector window is assembled
   * client-side. Every period is validated up front so a malformed key short-circuits before any
   * request leaves the client. An empty window emits `{}` (avoids the `forkJoin({})` never-emits
   * gotcha).
   */
  public usageWindow(
    periods: string[],
  ): Observable<Record<string, UsageMetricResponse[]>> {
    periods.forEach((period) => this.assertPeriod(period));
    if (periods.length === 0) return of({});
    return forkJoin(
      Object.fromEntries(periods.map((period) => [period, this.usage(period)])),
    );
  }

  /**
   * `POST /UsageMetrics/aggregate?period=` — recompute the period aggregate (admin).
   * Empty body; identity from the token.
   */
  public aggregate(period: string): Observable<void> {
    this.assertPeriod(period);
    return this.http.post<void>(
      `${this.baseUrl}/UsageMetrics/aggregate`,
      {},
      {
        params: new HttpParams().set('period', period),
        withCredentials: true,
      },
    );
  }

  /**
   * `POST /UsageMetrics/close?period=&ownerType=&ownerId=` — close the period for an
   * owner (admin). Enum travels by NAME. Empty body; identity from the token.
   */
  public close(
    period: string,
    ownerType: OwnerType,
    ownerId: string,
  ): Observable<void> {
    this.assertPeriod(period);
    return this.http.post<void>(
      `${this.baseUrl}/UsageMetrics/close`,
      {},
      {
        params: new HttpParams()
          .set('period', period)
          .set('ownerType', ownerType)
          .set('ownerId', ownerId),
        withCredentials: true,
      },
    );
  }

  /** Throws synchronously when `period` is not `YYYY-MM`. */
  private assertPeriod(period: string): void {
    if (!PERIOD_KEY_PATTERN.test(period)) {
      throw new Error('Invalid period; expected YYYY-MM');
    }
  }
}
